import { View, Text, Button, Card } from "@src/components";
import { router, Stack, useLocalSearchParams } from "expo-router";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { ActivityIndicator, ScrollView } from "react-native";
import { useCallback, useMemo } from "react";
import { Section } from "@src/features/settings/components/Section";
import { Icon } from "@src/components/Icon";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { ListRoleSelector, useManageCameraAccess } from "@visioni/a3s-access/app";
import { CameraAdminsList } from "@src/features/settings/components/CameraAdminsList";
import { CameraUsersList } from "@src/features/settings/components/CameraUsersList";
import { showErrorToast, showSuccessToast } from "@src/utils/toast";
import { isWeb } from "@src/utils/web-helpers";
import { formatCameraLabel } from "@src/utils/key-parser";
import { WebLayout } from "@src/components/web/WebLayout.web";

export default function ManageAccessScreen() {
  const { theme } = useUnistyles();
  const { headerLeft } = useHeaderBackButton();
  const { tenantId, siteId, cameraKey } = useLocalSearchParams<{
    tenantId?: string;
    siteId?: string;
    cameraKey: string;
  }>();

  const cameraLabel = useMemo(() => formatCameraLabel(cameraKey) || cameraKey, [cameraKey]);

  const { isLoading, cameraUsers, tenantAdmins, siteWideUsers, errorItemIds, handleCameraUserRoleChange } =
    useManageCameraAccess({
      siteId,
      cameraKey,
      onRoleChangeSuccess: (userName, role) =>
        showSuccessToast({
          message: "Role updated",
          description: `${userName} is now ${role.charAt(0).toUpperCase() + role.slice(1)}`,
        }),
      onRoleChangeError: () => showErrorToast({ message: "Failed to update role" }),
    });

  const headerTitle = useCallback(() => {
    return (
      <View flex={1} flexDirection="column" alignItems="center" justifyContent="space-between" pr="7xl">
        <Text textStyle="lg/semibold" color="text">
          Users & Permissions
        </Text>
        <View alignItems="center" gap="xxs">
          <Text textStyle="xs/medium">{cameraLabel}</Text>
        </View>
      </View>
    );
  }, [cameraLabel]);

  const handleNavigateToAddUsers = useCallback(() => {
    if (!tenantId || !siteId || !cameraKey) return;
    router.push({
      pathname: "/tenants/[tenantId]/sites/[siteId]/cameras/[cameraKey]/add-users-to-camera",
      params: {
        tenantId,
        siteId,
        cameraKey,
      },
    });
  }, [tenantId, siteId, cameraKey]);

  const handleNavigateToCameraAccess = useCallback((userId: string) => {
    router.push(`/settings/site-access/camera-access/${encodeURIComponent(userId)}`);
  }, []);

  // siteWideUsers entries use a composite id `${userId}:${role}` to keep a user that is both
  // manager and viewer rendered twice. Strip the role suffix before navigating.
  const handleSiteWideUserPress = useCallback(
    (compositeId: string) => {
      const [rawUserId] = compositeId.split(":");
      handleNavigateToCameraAccess(rawUserId);
    },
    [handleNavigateToCameraAccess],
  );

  const webContent = (
    <ScrollView style={styles.scrollView} contentContainerStyle={styles.contentContainerStyle}>
      {/* Web-only header showing the camera identity above the cards. The native screen
            tucks this into the navigation bar via `headerTitle`; on web the bar is hidden
            so we surface the same information here. */}
      <View flexDirection="row" alignItems="center" justifyContent="space-between">
        <Text textStyle="lg/semibold" color="text">
          Users & Permissions
        </Text>
        <View alignItems="center" gap="xxs">
          <Text textStyle="xs/medium">{cameraLabel}</Text>
        </View>
      </View>

      <View gap="3xl" flex={1} flexDirection="row" style={styles.cardRow}>
        <Card style={styles.card}>
          <Section title="Camera access" subtitle="These users only have access to this camera.">
            {isLoading ?
              <ActivityIndicator style={{ paddingVertical: theme.spacing["3xl"] }} />
            : cameraUsers.length > 0 ?
              <ListRoleSelector
                items={cameraUsers}
                onRoleChange={handleCameraUserRoleChange}
                onPressItem={handleNavigateToCameraAccess}
                iconName="chevronRight"
                errorItemIds={errorItemIds}
              />
            : <Text
                textStyle="sm/regular"
                color="textMuted"
                style={{ textAlign: "center", paddingVertical: theme.spacing["3xl"] }}
              >
                No users found
              </Text>
            }
            <Button
              size="md"
              variant="primary"
              label="Add someone"
              iconRight={<Icon name="plus" size={theme.spacing["5xl"]} color={theme.colors.white.default} />}
              onPress={handleNavigateToAddUsers}
              style={styles.button}
            />
          </Section>
        </Card>

        <Card style={styles.card}>
          {/* On native these two sections are nested inside a parent "Site-wide access" Section.
                On web the wider viewport lets us flatten them while keeping the same group label. */}
          <Section title="Site-wide access" subtitle="These users have access to all cameras on the site.">
            <Section title="Organisation admins">
              <CameraAdminsList users={tenantAdmins} onPress={handleNavigateToCameraAccess} />
            </Section>
            <Section title="Users" style={{ marginTop: theme.spacing["3xl"] }}>
              <CameraUsersList users={siteWideUsers} onPress={handleSiteWideUserPress} />
            </Section>
          </Section>
        </Card>
      </View>
    </ScrollView>
  );

  return (
    <>
      <Stack.Screen
        options={
          isWeb ?
            { headerShown: false }
          : {
              title: "Users & Permissions",
              headerTitle,
              headerLeft,
            }
        }
      />
      {isWeb ?
        <WebLayout>{webContent}</WebLayout>
      : <ScrollView style={styles.scrollView} contentContainerStyle={styles.contentContainerStyle}>
          <Section title="Camera access" subtitle="These users only have access to this camera.">
            {isLoading ?
              <ActivityIndicator style={{ paddingVertical: theme.spacing["3xl"] }} />
            : cameraUsers.length > 0 ?
              <ListRoleSelector
                items={cameraUsers}
                onRoleChange={handleCameraUserRoleChange}
                onPressItem={handleNavigateToCameraAccess}
                iconName="chevronRight"
                errorItemIds={errorItemIds}
              />
            : <Text
                textStyle="sm/regular"
                color="textMuted"
                style={{ textAlign: "center", paddingVertical: theme.spacing["3xl"] }}
              >
                No users found
              </Text>
            }
            <Button
              size="md"
              variant="primary"
              label="Add someone"
              iconRight={<Icon name="plus" size={theme.spacing["5xl"]} color={theme.colors.white.default} />}
              onPress={handleNavigateToAddUsers}
              style={styles.button}
            />
          </Section>
          <View style={styles.separator} />
          <Section title="Site-wide access" subtitle="These users have access to all cameras on the site.">
            <Section title="Organisation admins">
              <CameraAdminsList users={tenantAdmins} onPress={handleNavigateToCameraAccess} />
            </Section>
            <Section title="Users" style={{ marginTop: theme.spacing["3xl"] }}>
              <CameraUsersList users={siteWideUsers} onPress={handleSiteWideUserPress} />
            </Section>
          </Section>
        </ScrollView>
      }
    </>
  );
}

const styles = StyleSheet.create((theme) => ({
  scrollView: {
    flex: 1,
  },
  contentContainerStyle: {
    flexGrow: 1,
    padding: theme.spacing["3xl"],
    gap: theme.spacing["6xl"],
    paddingBottom: theme.spacing["12xl"],
  },

  button: {
    marginTop: theme.spacing["3xl"],
  },
  cardRow: {
    flexWrap: "wrap",
    alignItems: "flex-start",
  },
  card: {
    flexBasis: "48%",
    minWidth: 400,
    flexGrow: 1,
  },
  separator: {
    height: 1,
    backgroundColor: theme.colors.borderCard,
  },
}));
